import type { ImportPhase, ImportState } from '@ni/shared';

/**
 * **Who runs the planner's opening turn, and when** (import note §1.1, §1.2).
 *
 * The New import door used to run it: upload, then `POST /:id/open`, then
 * navigate. The upload is a few seconds and the turn is up to two minutes on a
 * real board, so the office pressed one button and watched a modal sit locked
 * for a minute with the new row already on the ledger behind it.
 *
 * So the door now closes on the upload and the **session page** owes the turn.
 * The rules are here, as two functions over plain fields, because they are the
 * whole of the change and a suite with no DOM can hold them.
 */

/** As much of a session as either answer below reads. */
export interface OpeningTurnSession {
  state: ImportState;
  phase: ImportPhase | null;
  messages: readonly unknown[];
  /**
   * **The finished session this one was started from** (§4.2), which is the one
   * way a session can hold a plan and still be owed its first turn. See
   * {@link needsOpeningTurn}.
   */
  retried_from: string | null;
}

/**
 * **Whether this session is owed its opening turn.**
 *
 * Three clauses, and each of them is a guard against firing a turn that costs
 * money on a page that is merely being looked at:
 *
 *  - **nothing has been said** — the transcript is the record of every turn ever
 *    run, so an empty one is the only proof that none has been. This is what
 *    survives a reload: come back to a session mid-conversation and the messages
 *    are there, so nothing fires.
 *  - **`profiled`** — the one phase that means "the file is read and nobody has
 *    asked the planner anything". A session waiting on the office, or holding a
 *    plan, has had its turn whatever the transcript looks like.
 *  - **`in_progress`** — a committed, canceled or unwound session is refused at
 *    the door anyway, and a turn that could only be refused should not be sent.
 *
 * **Or it is a retry sitting at `plan_ready`** (§4.2, 2026-09-14), which is the
 * one case the middle clause gets wrong. A retry lands holding the earlier
 * session's plan, so its phase is `plan_ready` from the moment it exists — and
 * the rule above reads that as "it has had its turn", leaving the page on an
 * empty transcript with a Retry button for a turn nobody ever asked for. The
 * transcript is still the proof that none has run; `retried_from` is what says
 * the plan came from somewhere other than a turn.
 *
 * **And `plan_ready` and nothing past it**, because `retried_from` is true for
 * the life of the session and the phase is not. A retry whose opening turn
 * failed can still be previewed and imported — the plan is there and was never
 * the problem — and a lineage clause on its own would then arm a turn on a
 * session at `importing`, where the planner answers `IMPORT_NOT_EDITABLE`: a
 * paid call on every reload, and a Retry button in the chat column that can
 * never succeed. `plan_ready` is the retry's own equivalent of `profiled` — the
 * session as it was handed over, before anybody did anything to it.
 */
export function needsOpeningTurn(session: OpeningTurnSession): boolean {
  return (
    session.state === 'in_progress' &&
    (session.phase === 'profiled' ||
      (session.retried_from !== null && session.phase === 'plan_ready')) &&
    session.messages.length === 0
  );
}

/**
 * **What the chat shows in place of a transcript**, given where the turn is.
 *
 * - `working` — it is in flight, or it is owed and the effect is about to send
 *   it. Both are the same thing to a reader, and folding them together is what
 *   keeps the first paint from flashing the failure line for one frame before
 *   the mutation reports itself pending.
 * - `retry` — it was asked and did not come back, or the session is sitting with
 *   an empty transcript in some phase this does not arm on. Either way the
 *   recourse is a person pressing the button.
 * - `null` — there is a transcript, so there is nothing to say about its absence.
 */
export type OpeningTurnState = 'working' | 'retry' | null;

export function openingTurnState(
  session: OpeningTurnSession,
  turn: { pending: boolean; failed: boolean },
): OpeningTurnState {
  if (session.state !== 'in_progress' || session.messages.length > 0) return null;
  if (turn.failed) return 'retry';
  if (turn.pending || needsOpeningTurn(session)) return 'working';
  return 'retry';
}
